
import { Grade, NowSchool, Subject, Translations } from '../Assignment';
import { DraftAssignmentRepo, DraftAssignment, AlreadyEditingAssignmentError } from './AssignmentDraft';
import { buildNewDraftAssignment, buildDraftAssignment, buildDraftAssignmentDTO } from './factory';
import { API } from 'utils/api';
import { bodyMessageOf, messageOf, statusOf } from 'utils/apiError';
import { STATUS_CONFLICT } from 'utils/constants';
import { AssignmentRequestDTO } from '../api';

import { Notification, NotificationTypes } from 'components/common/Notification/Notification';
import { Article } from 'assignment/Assignment';

/**
 * What `GET /api/teacher/assignments/draft/{id}/edit` returns as `assignmentContent`.
 *
 * Read from the backend rather than inferred. In skolerom-back,
 * `DraftAssignmentMapper::mapToEntityFromHttp` stores the request's `assignmentContent` verbatim,
 * `LiveSync` declares it `?array`, and `DraftAssignmentsTransformer` returns it unchanged. So it is
 * exactly what `buildAssignmentDTO` last sent — an object — or `null` for a draft never saved,
 * which is what `getDraftAssignmentById` branches on. It was declared `string`, which it has never
 * been.
 *
 * One difference from the request type. `AssignmentRequestDTO` declares `relatedArticles` as
 * `ArticleRequestDTO`, which has no `id`; but `buildRelatedArticleDTO` spreads each whole `Article`
 * in, so what is stored and returned carries every `Article` field, `id` included — which is why
 * the editor can find related articles by id after a reload. `Article` is a data class with no
 * methods, so these plain objects are Articles in every respect the type describes. The caveat is
 * nested: the builder slims `levels[].childArticles` to `{ title, wpId, levels }`.
 */
export type DraftAssignmentContentDTO = Omit<AssignmentRequestDTO, 'relatedArticles'> & {
  relatedArticles: Array<Article>;
};

export interface DraftAssignmentResponseDTO extends NewDraftAssignmentResponseDTO {
  isPrivate: boolean;
  isMySchool: boolean;
  inReview: boolean;
  mySchools: string | undefined;
  grades: Array<Grade>;
  subjects: Array<Subject>;
  levels: Array<number>;
  updatedAt: string;
}

export interface NewDraftAssignmentResponseDTO {
  id: number;
  uuid: string;
  assignmentContent: DraftAssignmentContentDTO | null;
  backgroundImage: string;
  isCopy: boolean;
  keywords?: Array<string>;
  grepCoreElementsIds?: Array<number>;
  grepMainTopicsIds?: Array<number>;
  grepGoalsIds?: Array<number>;
  // Array<number>, like every other declaration of this field: the request DTO below, the
  // Assignment it is built into, and the teaching-path draft's response.
  grepReadingInSubjectsIds?: Array<number>;
  sources?: Array<number>;
  guidance?: string;
  hasGuidance?: boolean;
  open?: boolean;
  schools?: Array<NowSchool>;
  localeId: number | null;
  isTranslations?: boolean;
  translations?: Array<Translations>;
  originalLocaleId?: number;
}

export interface DraftAssignmentRequestDTO {
  uuid: string;
  backgroundImage: string;
  title: string;
  description: string;
  guidance: string;
  numberOfQuestions: number;
  isPrivate: boolean;
  isMySchool: boolean;
  mySchools: string | undefined;
  assignmentContent: AssignmentRequestDTO;
  featuredImage?: string;
  subjects: Array<Subject>;
  grades: Array<Grade>;
  levels: Array<number>;
  isPublished?: boolean;
  isCopy?: boolean;
  inReview?: boolean;
  keywords?: Array<string>;
  grepCoreElementsIds?: Array<number>;
  grepMainTopicsIds?: Array<number>;
  grepGoalsIds?: Array<number>;
  grepReadingInSubjectsIds?: Array<number>;
  sources?: Array<number>;
  open?: boolean;
  localeId: number | null;
  isTranslations?: boolean;
  translations?: Array<Translations>;
  originalLocaleId?: number;
}

export class DraftAssignmentApi implements DraftAssignmentRepo {

  public async getNewAssignment(): Promise<DraftAssignment> {
    const result = (await API.get<NewDraftAssignmentResponseDTO>('api/teacher/assignments/create')).data;
    return buildNewDraftAssignment(result);
  }

  public async createAssignmentLocale(id: number, localeid?: number): Promise<DraftAssignment> {
    const result = (await API.get<NewDraftAssignmentResponseDTO>(
      `api/teacher/assignments/draft/${id}/create?localeId=${localeid}`)).data;
    return buildNewDraftAssignment(result);
  }

  public async getDraftAssignmentById(id: number, localeid?: number): Promise<DraftAssignment> {
    const response = (localeid && !isNaN(localeid)) ? await API.get<DraftAssignmentResponseDTO>(
      `/api/teacher/assignments/draft/${id}/edit?localeId=${localeid}`
    ) : await API.get<DraftAssignmentResponseDTO>(
      `/api/teacher/assignments/draft/${id}/edit`
    );
    return response.data.assignmentContent ?
      buildDraftAssignment(response.data) :
      buildNewDraftAssignment(response.data);
  }

  public async saveDraftAssignment(
    draftAssignment: DraftAssignment,
    localeId?: number
  ): Promise<string> {
    try {
      const mylocaleid = (localeId && !isNaN(localeId)) ? localeId : draftAssignment.localeId;
      const response = localeId ? await API.put<{ updateAt: string }>(
        `api/teacher/assignments/draft/${draftAssignment.id}?localeId=${mylocaleid}`,
        buildDraftAssignmentDTO(draftAssignment))
       : await API.put<{ updateAt: string }>(
        `api/teacher/assignments/draft/${draftAssignment.id}`,
        buildDraftAssignmentDTO(draftAssignment)
      );
      return response.data.updateAt;
    } catch (error) {
      if (statusOf(error) === STATUS_CONFLICT) {
        throw new AlreadyEditingAssignmentError();
      }

      throw error;
    }
  }

  public async getKeywordsFromArticles(arrayWpIds: Array<number>): Promise<Array<string>> {
    const idsString = arrayWpIds.toString();
    try {
      return (await API.get<Array<string>>(
        'api/teacher/assignments/getTags', {
          params: {
            articleIds: idsString,
          }
        })).data;
    } catch (error) {
      Notification.create({
        type: NotificationTypes.ERROR,
        title: bodyMessageOf(error) ?? messageOf(error)
      });

      // An empty list, not an implicit undefined. The service does `keywords.forEach(…)`
      // on this result, so falling out of the catch meant a failed fetch crashed with
      // "Cannot read properties of undefined" one frame later, replacing the notification
      // above with an unhandled error. `Promise<any>` was the only reason that compiled.
      return [];
    }
  }

  public async publishAssignment(draftAssignment: DraftAssignment): Promise<void> {
    try {
      await API.post(
        `api/teacher/assignments/${draftAssignment.id}`,
        buildDraftAssignmentDTO(draftAssignment)
      );
    } catch (error) {
      Notification.create({
        type: NotificationTypes.ERROR,
        title: bodyMessageOf(error) ?? messageOf(error)
      });
      // Rethrown: the Header navigates away on a publish that resolves.
      throw error;
    }
  }

  public async saveAttachment(assignmentId: number, attachmentId: number): Promise<void> {
    try {
      await API.post(`api/teacher/assignments/${assignmentId}/resource`, {
        wpId: attachmentId
      });
    } catch (e) {
      throw new Error(`unable to attach file ${messageOf(e)}`, { cause: e });
    }
  }

  public async removeAttachment(assignmentId: number, attachmentId: number): Promise<number> {
    try {
      const response = await API.delete(`api/teacher/assignments/${assignmentId}/resource`, {
        data: {
          wpId: attachmentId
        }
      });

      return response.status;

    } catch (e) {
      throw new Error(`unable to remove file ${messageOf(e)}`, { cause: e });
    }
  }
}
